import { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Autocomplete,
  AutocompleteItem,
  Card,
  CardBody,
  CardHeader,
  Input,
  Select,
  SelectItem,
  Switch,
  Textarea,
  toSlug,
} from '@structural-climbing/ui'
import { BrandingSettings } from './branding/BrandingSettings'
import { DAYS_OF_WEEK, DEFAULT_WORKING_HOURS } from './types'
import type { DayKey, WorkingHours } from './types'

export interface GymSettings {
  name: string
  slug: string | null
  description: string | null
  address: string | null
  phone: string | null
  email: string | null
  website: string | null
  image_url: string | null
  legal_name?: string | null
  cui: string | null
  reg_com: string | null
  iban: string | null
  primary_color: string | null
  secondary_color: string | null
  cover_url: string | null
  default_language: 'en' | 'ro'
  working_hours: WorkingHours | null
  timezone: string
}

interface Props {
  gymId: string
  initialValues: GymSettings
  onSave: (values: GymSettings) => Promise<void>
  activeTab: 'general' | 'branding' | 'billing'
}

export interface GymSettingsFormHandle {
  submit: () => void
}

export const GymSettingsForm = forwardRef<GymSettingsFormHandle, Props>(function GymSettingsForm(
  { gymId, initialValues, onSave, activeTab },
  ref,
) {
  const { t } = useTranslation()
  const [form, setForm] = useState<GymSettings>(initialValues)
  const [slugTouched, setSlugTouched] = useState(false)

  useImperativeHandle(ref, () => ({
    submit: () => void onSave(form),
  }))

  function set(field: keyof GymSettings, value: string) {
    setForm((prev) => {
      const next = { ...prev, [field]: value || null }
      if (field === 'name' && !slugTouched) {
        next.slug = toSlug(value)
      }
      return next
    })
  }

  function setDay(day: DayKey, field: 'open' | 'close' | 'closed', value: string | boolean) {
    setForm((prev) => {
      const current = prev.working_hours ?? DEFAULT_WORKING_HOURS
      return {
        ...prev,
        working_hours: {
          ...current,
          [day]: { ...current[day], [field]: value },
        },
      }
    })
  }

  function handleSlugChange(value: string) {
    setSlugTouched(true)
    setForm((prev) => ({ ...prev, slug: toSlug(value) || null }))
  }

  function handleLogoChange(url: string) {
    setForm((prev) => ({ ...prev, image_url: url }))
  }

  function handleCoverChange(url: string) {
    setForm((prev) => ({ ...prev, cover_url: url }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    void onSave(form)
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {activeTab === 'branding' && (
          <BrandingSettings
            gymId={gymId}
            name={form.name}
            logoUrl={form.image_url}
            coverUrl={form.cover_url}
            primaryColor={form.primary_color}
            secondaryColor={form.secondary_color}
            onLogoChange={handleLogoChange}
            onCoverChange={handleCoverChange}
            onPrimaryChange={(hex) => set('primary_color', hex)}
            onSecondaryChange={(hex) => set('secondary_color', hex)}
          />
        )}

        {activeTab === 'general' && (
          <>
            <Card>
              <CardHeader>
                <h2 className="text-base font-semibold">{t('settings.sectionGeneral')}</h2>
              </CardHeader>
              <CardBody className="flex flex-col gap-4">
                <Input
                  label={t('settings.gymNameLabel')}
                  value={form.name}
                  onValueChange={(v) => set('name', v)}
                  isRequired
                />
                <Input
                  label={t('settings.slugLabel')}
                  value={form.slug ?? ''}
                  onValueChange={handleSlugChange}
                  description={t('settings.slugDescription')}
                />
                <Textarea
                  label={t('settings.descriptionLabel')}
                  value={form.description ?? ''}
                  onValueChange={(v) => set('description', v)}
                  minRows={3}
                  maxRows={6}
                />
                <Select
                  label={t('settings.defaultLanguageLabel')}
                  selectedKeys={[form.default_language]}
                  onSelectionChange={(keys) => {
                    const val = Array.from(keys)[0] as string
                    if (val) set('default_language', val)
                  }}
                >
                  <SelectItem key="en">{t('settings.langEn')}</SelectItem>
                  <SelectItem key="ro">{t('settings.langRo')}</SelectItem>
                </Select>
                <Autocomplete
                  label={t('settings.timezoneLabel')}
                  selectedKey={form.timezone ?? 'Europe/Bucharest'}
                  onSelectionChange={(key) => {
                    if (key) set('timezone', String(key))
                  }}
                >
                  {Intl.supportedValuesOf('timeZone').map((tz) => (
                    <AutocompleteItem key={tz}>{tz}</AutocompleteItem>
                  ))}
                </Autocomplete>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h2 className="text-base font-semibold">{t('settings.sectionContact')}</h2>
              </CardHeader>
              <CardBody className="flex flex-col gap-4">
                <Input
                  label={t('settings.addressLabel')}
                  value={form.address ?? ''}
                  onValueChange={(v) => set('address', v)}
                />
                <Input
                  label={t('settings.phoneLabel')}
                  type="tel"
                  value={form.phone ?? ''}
                  onValueChange={(v) => set('phone', v)}
                />
                <Input
                  label={t('settings.emailLabel')}
                  type="email"
                  value={form.email ?? ''}
                  onValueChange={(v) => set('email', v)}
                />
                <Input
                  label={t('settings.websiteLabel')}
                  type="url"
                  value={form.website ?? ''}
                  onValueChange={(v) => set('website', v)}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h2 className="text-base font-semibold">{t('settings.sectionWorkingHours')}</h2>
              </CardHeader>
              <CardBody className="flex flex-col gap-3">
                {DAYS_OF_WEEK.map((day) => {
                  const schedule = (form.working_hours ?? DEFAULT_WORKING_HOURS)[day]
                  return (
                    <div key={day} className="flex items-center gap-3">
                      <span className="w-24 shrink-0 text-sm capitalize">
                        {t(`settings.wh.${day}`)}
                      </span>
                      <Switch
                        size="sm"
                        isSelected={!schedule.closed}
                        onValueChange={(v) => setDay(day, 'closed', !v)}
                        aria-label={t('settings.wh.open')}
                      />
                      <Input
                        type="time"
                        size="sm"
                        aria-label={t('settings.wh.open')}
                        value={schedule.open}
                        onValueChange={(v) => setDay(day, 'open', v)}
                        isDisabled={schedule.closed}
                        className="max-w-28"
                      />
                      <span className="text-sm text-foreground/50">–</span>
                      <Input
                        type="time"
                        size="sm"
                        aria-label={t('settings.wh.close')}
                        value={schedule.close}
                        onValueChange={(v) => setDay(day, 'close', v)}
                        isDisabled={schedule.closed}
                        className="max-w-28"
                      />
                      {schedule.closed && (
                        <span className="text-xs text-foreground/40">
                          {t('settings.wh.closed')}
                        </span>
                      )}
                    </div>
                  )
                })}
              </CardBody>
            </Card>
          </>
        )}

        {activeTab === 'billing' && (
          <Card>
            <CardHeader className="flex flex-col items-start gap-1">
              <h2 className="text-base font-semibold">{t('settings.sectionFiscal')}</h2>
              <p className="text-sm text-foreground/60">{t('settings.fiscalHint')}</p>
            </CardHeader>
            <CardBody className="flex flex-col gap-4">
              <Input
                label={t('settings.legalNameLabel')}
                description={t('settings.legalNameHint')}
                value={form.legal_name ?? ''}
                onValueChange={(v) => set('legal_name', v)}
              />
              <Input
                label={t('settings.cuiLabel')}
                value={form.cui ?? ''}
                onValueChange={(v) => set('cui', v)}
              />
              <Input
                label={t('settings.regComLabel')}
                value={form.reg_com ?? ''}
                onValueChange={(v) => set('reg_com', v)}
              />
              <Input
                label={t('settings.ibanLabel')}
                value={form.iban ?? ''}
                onValueChange={(v) => set('iban', v)}
              />
            </CardBody>
          </Card>
        )}
      </form>
    </>
  )
})
